import ErrorHandler from '../utils/ErrorHandler';
import Toast from 'react-native-toast-message';
import { supabase } from '../data/client';
import { createNotification } from './notifications';
import { getCurrentOrgID } from './organization';
import { insertActivity } from './activity';
import { getLatestEntitlements, isFreeOrg } from '@/utils/organizationEntitlements';

const getRegistrations = async () => {
    const orgID = await getCurrentOrgID();

    if(!orgID){
        return [];
    }

    const {data, error} = await supabase.rpc('get_registrations', {org_id: orgID});

    if(error){
        // 42501: request hit the RPC as anon (signed-out/expired session) —
        // expected denial, not an app bug (CHURCH-APP-R7).
        if (error.code !== '42501') {
            ErrorHandler(Error(error.message), {query: "getRegistrations", orgID, error}, `Something went wrong fetching the new user registrations`)
        }
        return [];
    }

    const potentialPeoplePromises = [];

    data?.forEach(r => {
        potentialPeoplePromises.push(fetchPotentialConnections(r));
    })

    await Promise.all(potentialPeoplePromises).then((results) => {
        results.forEach((peopleData, i) => {
            data[i].matchedPeople = peopleData;
        })
    })

    return !data ? [] : data;
}

const fetchPotentialConnections = async (r) => {
    const orgID = await getCurrentOrgID();

    if(!orgID){
        return [];
    }

    // Explicit columns only — People.customFieldValues is a paid-only column
    // boundary; select(*) would carry it pre-migration.
    const {error: peopleError, data: peopleData} = await supabase.from('People').select('id, firstName, lastName, email, avatarUpdatedAt, avatarStorageKey, isArchived, RegisteredUsersOrganizations(userID)')
    .eq('organizationID', orgID).eq('isArchived', false).or(`email.eq.${r.User?.email},and(firstName.eq.${r.User?.firstName}, lastName.eq.${r.User?.lastName})`);

    if(peopleError){
        ErrorHandler(Error(peopleError.message), {query: "getRegistrations", orgID, peopleError}, `Something went wrong fetching the potential connections for ${r.User?.firstName + " " + r.User?.lastName}`)
    }

    // Filter out people who already have a RegisteredUsersOrganizations entry (already registered users)
    const filteredPeople = peopleData?.filter(person => !person.RegisteredUsersOrganizations || person.RegisteredUsersOrganizations.length === 0);

    return filteredPeople || [];
}

const getRegistrationsByRequestingUser = async (requestingUserID) => {
    if(!requestingUserID) return [];

    const {data, error} = await supabase.from('Registrations').select('*, Organization:Organizations(*)').eq('requestingUser', requestingUserID);

    if(error){
        ErrorHandler(Error(error.message), {query: "getRegistrationsByRequestingUser", requestingUserID, error}, `Something went wrong fetching your pending registrations.`)
    }

    return data
}


const saveRegistration = async ({organizationHandle, user}) => {
    const {data, error: orgError} = await supabase.from('Organizations').select('*').eq('handle', organizationHandle).maybeSingle();

    if(orgError){
        ErrorHandler(Error(orgError.message), {mutation: "saveRegistration", user, organizationHandle, error: orgError}, `Something went wrong submitting your account request`)
        throw Error
    }
    else if(!data){
        Toast.show({type:'error', text2:"Looks like we couldn't find that organization. Make sure you have entered the correct handle!"})
        throw Error
    }


    const newRegistration = {
        organizationID: data.id,
        requestingUser: user.id,
    }


    const {error, data: registrationData} = await supabase.from('Registrations').insert(newRegistration).select('id').single();

    if(error){
        if(error.message.includes("duplicate key value")){
            Toast.show({type:'error', text2:"You have already requested to join this organization. An administrator will review your request, and get back with you as soon as possible."})
            return {duplicate: true};
        }

        ErrorHandler(Error(error.message), {mutation: "saveRegistration", user, organizationHandle, error}, `Something went wrong submitting your account request`)
        throw Error;
    }

    const notification = {
        type: "NEW_USER_REQUEST",
        path: `registrations`,
        organizationID: data.id,
        title: "New Pending User Request",
        targetPersonID: "",
        parameters: {
            registrationID: registrationData.id   
        },
        message: `${user.user_metadata.firstName} ${user.user_metadata.lastName} (${user.user_metadata.email}) has requested to join your organization`
    }
        
    createNotification(notification, true)  
        
    Toast.show({type:'success', text2:"Request submitted successfully! If your request is approved, you will receive an email with your login info."})
}


const deleteRegistration = async ({id, denied}) => {
    if (!id) return [];
    const orgID = await getCurrentOrgID();
    const {error} = await supabase.from('Registrations').delete().eq('id', id);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "deleteRegistrations", id, error}, "There was an error submitting your account request. Please try again later.")
        return [];
    }

    const {error: notificationError} = await supabase.from('Notifications').delete().eq('organizationID', orgID).eq('notificationType', "NEW_USER_REQUEST").eq('parameters->>registrationID', id);

    if(notificationError){
        ErrorHandler(Error(notificationError.message), {mutation: "deleteRegistrations-NotificationDelete", id, notificationError}, "There was an error submitting your account request. Please try again later.")
        return [];
    }

    if(denied){
        Toast.show({type:'success', text2:"The new user request has been denied."})
    }
}


const registerUserWithOrganization = async ({userID, person}) => {
    const orgID = await getCurrentOrgID();

    // Check if person is already registered in the organization (with same userID to match unique constraint)
    const {data: existingRegistration} = await supabase.from('RegisteredUsersOrganizations').select('id').eq('personID', person.id).eq('organizationID', orgID).eq('userID', userID).maybeSingle();
    
    if(existingRegistration){
        Toast.show({type: 'error', text2: "This person is already registered in your organization!"})
        throw Error("Person already registered in organization");
    }

    const {data, error} = await supabase.from('RegisteredUsersOrganizations').insert({userID, personID: person.id, organizationID: orgID, isAdmin: false}).select('Organization:Organizations(name)').single();
    
    if(error){
        if(error.message.includes('duplicate key value')){
            Toast.show({type: 'error', text2: "This person is already registered in your organization!"})
            throw Error("Person already registered in organization");
        }
        ErrorHandler(Error(error.message), {mutation: "registerUserWithOrganization", userID, personID: person.id, orgID, error}, "There was an error registering the new user. Please try again later.")
        throw Error
    }

    const { error:personError } = await supabase.from('People').update({firstName: person.firstName, lastName: person.lastName, email: person.email}).eq('id', person.id);

    if(personError){
        ErrorHandler(Error(personError.message), {mutation: "registerUserWithOrganization-PersonUpdate", userID, personID: person.id, orgID, personError})
        throw Error
    }

    createNotification({
        title: `Your request to join '${data.Organization?.name}' has been accepted`,
        path: `/`,
        targetPersonID: person.id,
        type: "ORG_REQUEST_ACCEPTED",
        message: `You have been added to Reach for ${data.Organization?.name} and can now login!`,
    })

    const personName = `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim();
    insertActivity({
        subjectType: 'Person',
        subjectID: person.id,
        subjectName: personName,
        verb: 'create',
        details: 'Accepted registration request',
    }).catch(() => {});

    return {...data, person}

}


const removeUserFromOrganization = async ({userID, personID}) => {
    const orgID = await getCurrentOrgID();

    const {error, data} = await supabase.from('RegisteredUsersOrganizations').delete().eq('userID', userID).eq('personID', personID).eq('organizationID', orgID).select('Person:People(firstName, lastName)').maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "removeUserFromOrganization", userID, personID, orgID, error}, "There was an error removing the user from your Organization. Please try again later.")
        throw Error
    }

    return data
}


const changeConnectedProfile = async ({userID, personID}) => {
    const orgID = await getCurrentOrgID();

    const {error, data} = await supabase.from('RegisteredUsersOrganizations').update({personID}).eq('userID', userID).eq('organizationID', orgID).select('id').maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "changeConnectedProfile", userID, personID, orgID, error}, "There was an error changing the connected profile. Please try again later.")
        throw Error
    }

    return data
}


const getRegisteredUsers = async () => {
    const orgID = await getCurrentOrgID();

    const roleCount = isFreeOrg(getLatestEntitlements()) ? '' : ', Roles(count)';
    const {data, error} = await supabase.from('RegisteredUsersOrganizations').select(`*, Person:People(id, firstName, lastName, email, avatarUpdatedAt, avatarStorageKey${roleCount})`).eq('organizationID', orgID).order('Person(firstName)', {ascending: true});

    if(error){
        ErrorHandler(Error(error.message), {query: "getRegisteredUsers", orgID, error}, "There was an error fetching registered users. Please try again later.")
       throw Error;
    }

    return data;
}


const sendInvitation = async ({ personID, email, firstName, organizationName, inviterName }) => {
    const orgID = await getCurrentOrgID();

    const { data, error } = await supabase.functions.invoke('send-invite', {
        body: {
            personID,
            email,
            firstName,
            organizationID: orgID,
            organizationName,
            inviterName
        }
    });

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "sendInvitation", personID, email, error }, "There was an error sending the invitation. Please try again later.");
        throw error;
    }

    if (data?.error) {
        Toast.show({ type: 'error', text2: data.error });
        throw Error(data.error);
    }

    Toast.show({ type: 'success', text2: `Invitation sent to ${email}` });
    return data;
}


const getInvitesForUser = async (email) => {
    if (!email) return [];

    const { data, error } = await supabase
        .from('RegistrationInvites')
        .select('*, Organization:Organizations(id, name, logoPublicUrl)')
        .eq('email', email.toLowerCase())
        .gt('expiresAt', new Date().toISOString())
        .order('createdAt', { ascending: false });

    if (error) {
        ErrorHandler(Error(error.message), { query: "getInvitesForUser", email, error }, "Something went wrong fetching your invitations.");
        return [];
    }

    return data || [];
}


const getInviteByToken = async (inviteToken) => {
    const { data, error } = await supabase
        .from('RegistrationInvites')
        .select('*, Organization:Organizations(id, name, logoPublicUrl), Person:People!RegistrationInvites_personID_fkey(id, firstName, lastName)')
        .eq('inviteToken', inviteToken)
        .gt('expiresAt', new Date().toISOString())
        .maybeSingle();

    if (error) {
        ErrorHandler(Error(error.message), { query: "getInviteByToken", inviteToken, error }, "There was an error fetching the invitation.");
        return null;
    }

    return data;
}


const getInvites = async () => {
    const orgID = await getCurrentOrgID();

    const { data, error } = await supabase
        .from('RegistrationInvites')
        .select('*, Person:People!RegistrationInvites_personID_fkey(id, firstName, lastName, avatarUpdatedAt), InvitedBy:People!RegistrationInvites_invitedByID_fkey(id, firstName, lastName)')
        .eq('organizationID', orgID)
        .order('createdAt', { ascending: false });

    if (error) {
        ErrorHandler(Error(error.message), { query: "getInvites", orgID, error }, "Something went wrong fetching invites.");
        return [];
    }

    return data || [];
}


const withdrawInvite = async ({ id }) => {
    const { error } = await supabase.from('RegistrationInvites').delete().eq('id', id);

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "withdrawInvite", id, error }, "There was an error withdrawing the invitation.");
        throw Error(error.message);
    }

    Toast.show({ type: 'success', text2: 'Invitation withdrawn successfully.' });
}


const acceptInvite = async ({ inviteID, organizationID, personID, userID }) => {
    if (!inviteID || !organizationID || !userID) {
        Toast.show({ type: 'error', text2: 'This invitation is invalid or has expired.' });
        throw Error('Invalid or expired invitation');
    }

    // Add user to organization
    const { error: regError } = await supabase.from('RegisteredUsersOrganizations').insert({
        userID,
        personID: personID || null,
        organizationID,
        isAdmin: false
    });

    if (regError) {
        if (regError.message.includes('duplicate')) {
            Toast.show({ type: 'info', text2: 'You are already a member of this organization.' });
        } else {
            ErrorHandler(Error(regError.message), { mutation: "acceptInvite", inviteID, userID, regError }, "There was an error accepting the invitation.");
            throw regError;
        }
    }

    // Delete the invite record
    await supabase.from('RegistrationInvites').delete().eq('id', inviteID);

    return { id: inviteID, organizationID, personID };
}


export { 
    saveRegistration,
    getRegistrations,
    deleteRegistration,
    registerUserWithOrganization,
    getRegistrationsByRequestingUser,
    getRegisteredUsers,
    removeUserFromOrganization,
    changeConnectedProfile,
    sendInvitation,
    getInviteByToken,
    getInvites,
    getInvitesForUser,
    withdrawInvite,
    acceptInvite
}